import {useApolloClient} from '@apollo/client';
import FIREBASE_ERROR_CODE from '@src/Api/Constants/firebase.error';
import {
  useResetPasswordMutation,
  useLazyRemoveMemberQuery,
  useLazyTraceSignUpQuery,
  useLazyCheckMemberEmailExistQuery,
  useLazyGetMemberByUsernameQuery,
} from '@src/Api/TraceApi/traceApi';
import useTracker from '@src/Services/Amplitude/hooks/useTracker';
import {
  appleProvider,
  auth,
  facebookProvider,
  googleProvider,
} from '@src/firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  FacebookAuthProvider,
  OAuthProvider,
  fetchSignInMethodsForEmail,
  UserCredential,
  signInWithCustomToken,
  sendPasswordResetEmail,
  linkWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import {toast} from 'react-toastify';
import {navigate} from 'gatsby';
import {useAppDispatch, useAppSelector} from '@src/Store/hooks';
import appAuthSlice from '@src/Store/Slices/AppSlice/auth.slice';
import {useCurrentLanguage} from '@src/Store/Slices/LanguageSlice/Language.selector';
import {languageMapper} from '@src/tools/languageMapper';
import memberSlice from '@src/Store/Slices/Member.slice';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import {useSession} from '@src/Providers/SessionProvider';
import useNavigation from '@src/Navigation/useNavigation';

export interface IAuthForm {
  email: string;
  password: string;
}

export interface IFirebaseAuthenticationResponse {
  status: 'success' | 'error';
  user: UserCredential['user'] | null;
  errorMessage: string | null;
}

const useFireBaseAuthentication = () => {
  const client = useApolloClient();
  const {t} = useTranslation('auth');
  const [traceSignUp] = useLazyTraceSignUpQuery();
  const currentLang = useCurrentLanguage();
  const tracker = useTracker();
  const [removeMember] = useLazyRemoveMemberQuery();
  const [resetPassword] = useResetPasswordMutation();
  const dispatch = useAppDispatch();
  const {openAuthenticationModal} = useAppSelector(state => state.appAuth);
  const [checkMemberEmailExist] = useLazyCheckMemberEmailExistQuery();
  const session = useSession();
  const [fetchMemberByUsernameAndUpdateStore] =
    useLazyGetMemberByUsernameQuery();
  const rootNavigation = useNavigation();

  const {setSessionManuallyInitialized, setIsLoggedIn, isLoggedIn} =
    useIsLoggedIn();

  // Helper function for navigation logic, similar to the one in AuthProvider
  const handleNavigation = async () => {
    console.log(
      '🚀 ~ handleNavigation ~ redirect:',
      openAuthenticationModal?.redirect,
    );
    const currentPath = window.location.pathname;

    if (openAuthenticationModal?.redirect) {
      const redirectUrl = openAuthenticationModal.redirect;

      // Check if it's a full URL (starts with http/https)
      if (
        redirectUrl.startsWith('http://') ||
        redirectUrl.startsWith('https://')
      ) {
        // Check if it's the same domain
        const url = new URL(redirectUrl);
        if (url.origin === window.location.origin) {
          // Same domain, use Gatsby navigation with pathname
          await rootNavigation.navigate(url.pathname, {
            replace: true,
          });
        } else {
          // Different domain, do a full page redirect
          window.location.href = redirectUrl;
        }
      } else {
        // For same-domain paths, use Gatsby navigation
        await rootNavigation.navigate(redirectUrl, {
          replace: true,
        });
      }

    } else {
      // Handle default navigation first
      if (currentPath.includes('/tv-auth')) {
        await rootNavigation.navigate(currentPath, {
          replace: true,
        });
      }
    }
  };

  const isMemberEmailExist = async (email: string) => {
    try {
      const {data} = await checkMemberEmailExist(email);
      return !!data;
    } catch (error) {
      console.log('error', error);
      return false;
    }
  };

  const isEmailExist = async (email: string) => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      console.log('methods', methods);
      return methods.length > 0;
    } catch (error) {
      console.log('error', error);
    }
  };

  // Common authentication process shared between all sign-in methods
  const processAuthentication = async (
    user: UserCredential['user'],
    isSignUp: boolean = false,
  ): Promise<IFirebaseAuthenticationResponse> => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'success',
      user: user,
    };

    if (!user || !user.email) {
      response.status = 'error';
      response.errorMessage = t('Invalid user data');
      setSessionManuallyInitialized(false);
      return response;
    }

    try {
      // Only check if member exists for login flow
      if (!isSignUp) {
        const isMemberExist = await isMemberEmailExist(user.email);
        if (!isMemberExist) {
          response.status = 'error';
          response.errorMessage = t('Member not found');
          setSessionManuallyInitialized(false);
          return response;
        }
      }

      // Initialize session
      const sessionInitialized = await session.initializeSession();

      if (!sessionInitialized) {
        response.status = 'error';
        response.errorMessage = t('Session initialization failed');
        setSessionManuallyInitialized(false);
        await auth.signOut();
        return response;
      }

      // Fetch member data to confirm
      const {data: memberData} = await fetchMemberByUsernameAndUpdateStore();

      if (
        memberData &&
        memberData.email &&
        user.email &&
        memberData.email.toLowerCase() === user.email.toLowerCase()
      ) {
        // Success
        response.status = 'success';
        response.errorMessage = null;

        // Set isLoggedIn=true since we've verified everything
        setIsLoggedIn(true);

        // Reset the manual flag since we're done with the process
        setSessionManuallyInitialized(false);

        // Handle any necessary navigation
        await handleNavigation();
      } else {
        response.status = 'error';
        response.errorMessage = t('Member verification failed');
        setSessionManuallyInitialized(false);
        await auth.signOut();
      }
    } catch (error: any) {
      console.log('Error during authentication process:', error);
      response.status = 'error';
      response.errorMessage = t('Authentication process failed');
      setSessionManuallyInitialized(false);
      await auth.signOut();
    }

    return response;
  };

  const signUp = async (
    formData: any,
  ): Promise<IFirebaseAuthenticationResponse> => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'success',
      user: null,
    };

    const singUpResponse = await traceSignUp(formData);

    if (singUpResponse.error) {
      response.status = 'error';
      const error = singUpResponse.error as any;
      console.log('error', error);
      if (error.data && error.data.message) {
        if (Array.isArray(error.data.message)) {
          response.errorMessage = error.data.message.join('\n');
        } else {
          response.errorMessage = error.data.message;
        }
      } else {
        response.errorMessage = t('Sign up failed !');
      }
    } else {
      if (singUpResponse && singUpResponse.data && singUpResponse.data.token) {
        dispatch(appAuthSlice.actions.setAuthType('login'));
        try {
          // Set the flag BEFORE authentication to prevent duplicate session initialization
          setSessionManuallyInitialized(true);

          const userCredential = await signInWithCustomToken(
            auth,
            singUpResponse.data.token,
          );

          // Use the shared authentication process, but in signup mode
          const authResult = await processAuthentication(
            userCredential.user,
            true,
          );

          // Copy the result to our response
          response.status = authResult.status;
          response.errorMessage = authResult.errorMessage;
          response.user = authResult.user;
        } catch (error) {
          console.log('error', error);
          response.status = 'error';
          response.errorMessage = t('Sign up failed !');
          setSessionManuallyInitialized(false);
        }
      } else {
        response.status = 'error';
        response.errorMessage = t('Sign up failed !');
      }
    }
    return response;
  };

  const signUpWithMail = async (formData: IAuthForm) => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'success',
      user: null,
    };

    try {
      // Set the flag BEFORE authentication to prevent duplicate session initialization
      setSessionManuallyInitialized(true);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password,
      );

      // Use the shared authentication process, but in signup mode
      return await processAuthentication(userCredential.user, true);
    } catch (error: any) {
      console.log('error', error);
      response.status = 'error';
      response.errorMessage = t('Sign up failed !');
      setSessionManuallyInitialized(false);
    }

    return response;
  };

  const signIn = async (
    formData: IAuthForm,
  ): Promise<IFirebaseAuthenticationResponse> => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'success',
      user: null,
    };

    try {
      // Step 1: Verify if member exists first to fail fast
      const isMemberExist = await isMemberEmailExist(formData.email);
      if (!isMemberExist) {
        response.status = 'error';
        response.errorMessage = t('Member not found');
        return response;
      }

      // Set the flag BEFORE authentication to prevent duplicate session initialization
      // This will ensure onAuthStateChanged knows a manual initialization is coming
      setSessionManuallyInitialized(true);

      // Step 2: Authenticate with Firebase
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password,
      );

      // Process authentication with the common function
      return await processAuthentication(userCredential.user);
    } catch (error: any) {
      response.status = 'error';
      // Reset the flag since authentication failed
      setSessionManuallyInitialized(false);
      if (error.code === FIREBASE_ERROR_CODE.userNotFound) {
        response.errorMessage = t('User not found !');
      } else if (error.code === FIREBASE_ERROR_CODE.wrongPassword) {
        response.errorMessage = t('Wrong password !');
      } else {
        response.errorMessage = t('Sign in failed !');
      }
    }

    return response;
  };

  const signInWithGoogle = async (
    signUp?: boolean,
  ): Promise<IFirebaseAuthenticationResponse> => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'error',
      user: null,
    };
    try {
      // Set the flag BEFORE authentication to prevent duplicate session initialization
      if (!signUp) {
        setSessionManuallyInitialized(true);
      }

      const currentUser = auth.currentUser;
      
      // Si l'utilisateur est anonyme, essayer de lier le compte
      if (currentUser && currentUser.isAnonymous) {
        console.log('🔗 [Google Auth] Linking anonymous user with Google account');
        
        try {
          // Obtenir les credentials Google via popup
          const result = await signInWithPopup(auth, googleProvider);
          const credential = GoogleAuthProvider.credentialFromResult(result);
          
          if (credential && result.user) {
            // Lier le compte anonyme avec Google
            const linkedUser = await linkWithCredential(currentUser, credential);
            console.log('✅ [Google Auth] Anonymous user successfully linked');
            
            // Process authentication with the common function
            return await processAuthentication(linkedUser.user, signUp);
          }
        } catch (linkError: any) {
          console.log('🔄 [Google Auth] Link failed, trying direct sign-in:', linkError.code);
          
          // Si le compte Google existe déjà, se connecter directement
          if (linkError.code === 'auth/credential-already-in-use') {
            console.log('🔄 [Google Auth] Credential already in use, signing in with existing account');
            
            // Supprimer l'utilisateur anonyme d'abord
            try {
              await currentUser.delete();
              console.log('🗑️ [Google Auth] Anonymous user deleted');
            } catch (deleteError) {
              console.log('⚠️ [Google Auth] Could not delete anonymous user:', deleteError);
            }
            
            // Se connecter avec le compte Google existant
            const signInResult = await signInWithPopup(auth, googleProvider);
            if (signInResult.user) {
              console.log('✅ [Google Auth] Signed in with existing Google account');
              return await processAuthentication(signInResult.user, signUp);
            }
          }
          
          // Re-lancer l'erreur si ce n'est pas credential-already-in-use
          throw linkError;
        }
      } else {
        // Connexion normale (pas d'utilisateur anonyme)
        const result = await signInWithPopup(auth, googleProvider);
        if (result) {
          const credential = GoogleAuthProvider.credentialFromResult(result);

          if (credential) {
            // Process authentication with the common function
            return await processAuthentication(result.user, signUp);
          }
        }
      }

      // If we reached here, something went wrong
      response.status = 'error';
      response.errorMessage = t('Google auth error');
      if (!signUp) {
        setSessionManuallyInitialized(false);
      }
    } catch (error: any) {
      console.log('Google auth error', error);
      response.status = 'error';
      
      // Gestion spécifique des erreurs de liaison
      if (error.code === 'auth/credential-already-in-use') {
        response.errorMessage = t('This Google account is already linked to another user');
      } else if (error.code === 'auth/email-already-in-use') {
        response.errorMessage = t('Email already in use');
      } else {
        response.errorMessage = t('Google auth error');
      }
      
      if (!signUp) {
        setSessionManuallyInitialized(false);
      }
    }
    return response;
  };

  const signInWithFacebook = async (
    signUp?: boolean,
  ): Promise<IFirebaseAuthenticationResponse> => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'error',
      user: null,
    };
    try {
      // Set the flag BEFORE authentication to prevent duplicate session initialization
      if (!signUp) {
        setSessionManuallyInitialized(true);
      }

      const currentUser = auth.currentUser;
      
      // Si l'utilisateur est anonyme, essayer de lier le compte
      if (currentUser && currentUser.isAnonymous) {
        console.log('🔗 [Facebook Auth] Linking anonymous user with Facebook account');
        
        try {
          // Obtenir les credentials Facebook via popup
          const result = await signInWithPopup(auth, facebookProvider);
          const credential = FacebookAuthProvider.credentialFromResult(result);
          
          if (credential && result.user) {
            // Lier le compte anonyme avec Facebook
            const linkedUser = await linkWithCredential(currentUser, credential);
            console.log('✅ [Facebook Auth] Anonymous user successfully linked');
            
            // Process authentication with the common function
            return await processAuthentication(linkedUser.user, signUp);
          }
        } catch (linkError: any) {
          console.log('🔄 [Facebook Auth] Link failed, trying direct sign-in:', linkError.code);
          
          // Si le compte Facebook existe déjà, se connecter directement
          if (linkError.code === 'auth/credential-already-in-use') {
            console.log('🔄 [Facebook Auth] Credential already in use, signing in with existing account');
            
            // Supprimer l'utilisateur anonyme d'abord
            try {
              await currentUser.delete();
              console.log('🗑️ [Facebook Auth] Anonymous user deleted');
            } catch (deleteError) {
              console.log('⚠️ [Facebook Auth] Could not delete anonymous user:', deleteError);
            }
            
            // Se connecter avec le compte Facebook existant
            const signInResult = await signInWithPopup(auth, facebookProvider);
            if (signInResult.user) {
              console.log('✅ [Facebook Auth] Signed in with existing Facebook account');
              return await processAuthentication(signInResult.user, signUp);
            }
          }
          
          // Re-lancer l'erreur si ce n'est pas credential-already-in-use
          throw linkError;
        }
      } else {
        // Connexion normale (pas d'utilisateur anonyme)
        const result = await signInWithPopup(auth, facebookProvider);
        if (result) {
          const credential = FacebookAuthProvider.credentialFromResult(result);

          if (credential) {
            // Process authentication with the common function
            return await processAuthentication(result.user, signUp);
          }
        }
      }

      // If we reached here, something went wrong
      response.status = 'error';
      response.errorMessage = t('Facebook auth error');
      if (!signUp) {
        setSessionManuallyInitialized(false);
      }
    } catch (error: any) {
      console.log('Facebook auth error', error);
      response.status = 'error';
      
      // Gestion spécifique des erreurs de liaison
      if (error.code === 'auth/credential-already-in-use') {
        response.errorMessage = t('This Facebook account is already linked to another user');
      } else if (error.code === 'auth/email-already-in-use') {
        response.errorMessage = t('Email already in use');
      } else {
        response.errorMessage = t('Facebook auth error');
      }
      
      if (!signUp) {
        setSessionManuallyInitialized(false);
      }
    }
    return response;
  };

  const signInWithApple = async (
    signUp?: boolean,
  ): Promise<IFirebaseAuthenticationResponse> => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'error',
      user: null,
    };
    appleProvider.addScope('email');
    appleProvider.addScope('name');

    try {
      // Set the flag BEFORE authentication to prevent duplicate session initialization
      if (!signUp) {
        setSessionManuallyInitialized(true);
      }

      const currentUser = auth.currentUser;
      
      // Si l'utilisateur est anonyme, essayer de lier le compte
      if (currentUser && currentUser.isAnonymous) {
        console.log('🔗 [Apple Auth] Linking anonymous user with Apple account');
        
        try {
          // Obtenir les credentials Apple via popup
          const result = await signInWithPopup(auth, appleProvider);
          const credential = OAuthProvider.credentialFromResult(result);
          
          if (credential && result.user) {
            // Lier le compte anonyme avec Apple
            const linkedUser = await linkWithCredential(currentUser, credential);
            console.log('✅ [Apple Auth] Anonymous user successfully linked');
            
            // Process authentication with the common function
            return await processAuthentication(linkedUser.user, signUp);
          }
        } catch (linkError: any) {
          console.log('🔄 [Apple Auth] Link failed, trying direct sign-in:', linkError.code);
          
          // Si le compte Apple existe déjà, se connecter directement
          if (linkError.code === 'auth/credential-already-in-use') {
            console.log('🔄 [Apple Auth] Credential already in use, signing in with existing account');
            
            // Supprimer l'utilisateur anonyme d'abord
            try {
              await currentUser.delete();
              console.log('🗑️ [Apple Auth] Anonymous user deleted');
            } catch (deleteError) {
              console.log('⚠️ [Apple Auth] Could not delete anonymous user:', deleteError);
            }
            
            // Se connecter avec le compte Apple existant
            const signInResult = await signInWithPopup(auth, appleProvider);
            if (signInResult.user) {
              console.log('✅ [Apple Auth] Signed in with existing Apple account');
              return await processAuthentication(signInResult.user, signUp);
            }
          }
          
          // Re-lancer l'erreur si ce n'est pas credential-already-in-use
          throw linkError;
        }
      } else {
        // Connexion normale (pas d'utilisateur anonyme)
        const result = await signInWithPopup(auth, appleProvider);
        const credential = OAuthProvider.credentialFromResult(result);

        if (credential) {
          // Process authentication with the common function
          return await processAuthentication(result.user, signUp);
        }
      }

      // If we reached here, something went wrong
      response.status = 'error';
      response.errorMessage = t('Apple auth error');
      if (!signUp) {
        setSessionManuallyInitialized(false);
      }
    } catch (error: any) {
      console.log('Apple auth error', error);
      response.status = 'error';
      
      // Gestion spécifique des erreurs de liaison
      if (error.code === 'auth/credential-already-in-use') {
        response.errorMessage = t('This Apple account is already linked to another user');
      } else if (error.code === 'auth/email-already-in-use') {
        response.errorMessage = t('Email already in use');
      } else {
        response.errorMessage = t('Apple auth error');
      }
      
      if (!signUp) {
        setSessionManuallyInitialized(false);
      }
    }

    return response;
  };

  const signOut = async () => {
    try {
      await auth.signOut();
      localStorage.setItem('ft', '');
      googleProvider.setCustomParameters({prompt: 'select_account'});
      facebookProvider.setCustomParameters({prompt: 'select_account'});
      appleProvider.setCustomParameters({prompt: 'select_account'});
      dispatch(memberSlice.actions.setMember(undefined));

      const currentLocation = window.location.pathname;

      if (currentLocation?.includes('/app')) {
        try {
          try {
            client.stop();
            await client.clearStore();
            await client.resetStore();
          } catch (error) {
            console.log('🚀 ~ signOut ~ resetStore error:', error);
          }
        } catch (error) {
          console.log('🚀 ~ signOut ~ error:', error);
        }
      }
    } catch (error) {
      console.log('🚀 ~ signOut ~ error:', error);
      // toast.error(t('Sign out failed !'));
    }
  };

  const isConnected = async () => {
    return !!auth.currentUser;
  };

  const traceForgotPassword = async (email: string) => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'success',
      user: null,
    };

    await resetPassword({email, lang: languageMapper(currentLang)})
      .unwrap()
      .then(() => {
        response.status = 'success';
        response.errorMessage = null;
      })
      .catch(error => {
        response.status = 'error';

        if (error.status === 404) {
          response.errorMessage = t('Email not found !');
        } else {
          response.errorMessage = t('Reset password failed !');
        }
      });
    return response;
  };

  const forgotPassword = async (
    email: string,
  ): Promise<IFirebaseAuthenticationResponse> => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'success',
      user: null,
    };
    await sendPasswordResetEmail(auth, email)
      .then(() => {
        response.status = 'success';
        response.errorMessage = null;
      })
      .catch(error => {
        response.status = 'error';

        if (error.code === FIREBASE_ERROR_CODE.userNotFound) {
          response.errorMessage = t('User not found !');
        } else {
          response.errorMessage = t('Forgot password failed !');
        }
      });

    return response;
  };

  const convertAnonymousUser = async (
    formData: IAuthForm,
  ): Promise<IFirebaseAuthenticationResponse> => {
    const response: IFirebaseAuthenticationResponse = {
      errorMessage: null,
      status: 'success',
      user: null,
    };

    try {
      const currentUser = auth.currentUser;
      
      if (!currentUser || !currentUser.isAnonymous) {
        response.status = 'error';
        response.errorMessage = t('No anonymous user to convert');
        return response;
      }

      // Créer les credentials email/password
      const credential = EmailAuthProvider.credential(
        formData.email,
        formData.password
      );

      // Lier le compte anonyme avec les credentials email/password
      const userCredential = await linkWithCredential(currentUser, credential);
      
      // Process authentication with the common function
      return await processAuthentication(userCredential.user, false);
    } catch (error: any) {
      response.status = 'error';
      
      if (error.code === 'auth/email-already-in-use') {
        response.errorMessage = t('Email already in use');
      } else if (error.code === 'auth/weak-password') {
        response.errorMessage = t('Password is too weak');
      } else {
        response.errorMessage = t('Failed to convert anonymous user');
      }
      
      console.error('Erreur lors de la conversion de l\'utilisateur anonyme:', error);
    }

    return response;
  };

  const deleteCurrentUser = async (formData: IAuthForm) => {
    try {
      // Check if user is logged in
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error(t('No user is currently logged in'));
        return undefined;
      }

      // Check authentication providers
      const providers = currentUser.providerData.map(
        provider => provider.providerId,
      );

      // Check if using email/password or social auth
      const isEmailProvider =
        providers.includes('password') && providers.length === 1;
      const isGoogleProvider = providers.includes('google.com');
      const isFacebookProvider = providers.includes('facebook.com');
      const isAppleProvider = providers.includes('apple.com');

      if (isEmailProvider) {
        // Handle email/password authentication deletion
        const {email, password} = formData;
        await signInWithEmailAndPassword(auth, email, password)
          .then(async userCredential => {
            if (userCredential.user) {
              await removeMember();
              await auth.currentUser?.delete();
              await signOut();
            } else {
              toast.error(t('Password error'));
            }
          })
          .catch(error => {
            console.log('error', error);
            if (error.code === FIREBASE_ERROR_CODE.userNotFound) {
              toast.error(t('User not found !'));
            } else if (error.code === FIREBASE_ERROR_CODE.wrongPassword) {
              toast.error(t('Wrong password !'));
            } else {
              toast.error(t('Something went wrong !'));
            }
          });
      } else if (isGoogleProvider || isFacebookProvider || isAppleProvider) {
        // For social providers, we need to reauthenticate first
        try {
          let provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider | null = null;
          if (isGoogleProvider) {
            provider = googleProvider;
          } else if (isFacebookProvider) {
            provider = facebookProvider;
          } else if (isAppleProvider) {
            provider = appleProvider;
          }

          if (provider) {
            // Reauthenticate with the appropriate provider
            const result = await signInWithPopup(auth, provider);
            if (result && result.user) {
              await removeMember();
              await auth.currentUser?.delete();
              await signOut();
            }
          } else {
            toast.error(t('Provider not supported for deletion'));
          }
        } catch (error) {
          console.log('error on social auth deletion', error);
          toast.error(t('Failed to delete social account'));
        }
      } else {
        toast.error(t('Unknown authentication provider'));
      }

      return undefined;
    } catch (error) {
      tracker.errorEncountered('user', 'Error on user remove');
      throw error;
    }
  };

  return {
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    isEmailExist,
    signUpWithMail,
    isConnected,
    forgotPassword,
    deleteCurrentUser,
    traceForgotPassword,
    handleNavigation,
    convertAnonymousUser,
  };
};

export default useFireBaseAuthentication;
